import { useState } from "react";
import { Ticket, Plus, Trash2, Copy, Check, Unlock } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { usePromoCodes, useCreatePromoCode, useUpdatePromoCode, useDeletePromoCode, usePromoAutoUnlockStats, PromoCode } from "@/hooks/usePromoCodes";
import { format } from "date-fns";
import { ru } from "date-fns/locale";

export default function PromoCodes() {
  const { data: promoCodes, isLoading } = usePromoCodes();
  const { data: autoUnlockData } = usePromoAutoUnlockStats();
  const createPromoCode = useCreatePromoCode();
  const updatePromoCode = useUpdatePromoCode();
  const deletePromoCode = useDeletePromoCode();

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [newCode, setNewCode] = useState("");
  const [newHitsAmount, setNewHitsAmount] = useState("5");
  const [newMaxUses, setNewMaxUses] = useState("");
  const [newExpiresAt, setNewExpiresAt] = useState("");
  const [newIsSingleUse, setNewIsSingleUse] = useState(true);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const handleCreate = async () => {
    if (!newCode.trim()) {
      toast.error("Введите код промокода");
      return;
    }

    const hitsAmount = parseInt(newHitsAmount);
    if (isNaN(hitsAmount) || hitsAmount < 1) {
      toast.error("Количество хитов должно быть больше 0");
      return;
    }

    try {
      await createPromoCode.mutateAsync({
        code: newCode.trim().toUpperCase(),
        hits_amount: hitsAmount,
        max_uses: newMaxUses ? parseInt(newMaxUses) : null,
        is_single_use: newIsSingleUse,
        expires_at: newExpiresAt || null,
      });
      
      toast.success("Промокод создан!");
      setIsCreateDialogOpen(false);
      setNewCode("");
      setNewHitsAmount("5");
      setNewMaxUses("");
      setNewExpiresAt("");
      setNewIsSingleUse(true);
    } catch (error: any) {
      if (error.message?.includes("duplicate")) {
        toast.error("Промокод с таким кодом уже существует");
      } else {
        toast.error("Ошибка при создании промокода");
      }
    }
  };

  const handleToggleActive = async (promoCode: PromoCode) => {
    try {
      await updatePromoCode.mutateAsync({
        id: promoCode.id,
        is_active: !promoCode.is_active,
      });
      toast.success(promoCode.is_active ? "Промокод деактивирован" : "Промокод активирован");
    } catch (error) {
      toast.error("Ошибка при обновлении промокода");
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Удалить промокод?")) return;
    
    try {
      await deletePromoCode.mutateAsync(id);
      toast.success("Промокод удалён");
    } catch (error) {
      toast.error("Ошибка при удалении промокода");
    }
  };

  const handleCopyCode = async (code: string, id: string) => {
    await navigator.clipboard.writeText(code);
    setCopiedId(id);
    toast.success("Код скопирован!");
    setTimeout(() => setCopiedId(null), 2000);
  };

  const generateRandomCode = () => {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    let result = "";
    for (let i = 0; i < 8; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setNewCode(result);
  };

  // Count auto-unlocks per promo code
  const autoUnlocksByPromo = new Map<string, number>();
  autoUnlockData?.forEach((u: any) => {
    const count = autoUnlocksByPromo.get(u.promo_code_id) || 0;
    autoUnlocksByPromo.set(u.promo_code_id, count + 1);
  });

  const stats = {
    total: promoCodes?.length || 0,
    active: promoCodes?.filter(p => p.is_active).length || 0,
    totalUses: promoCodes?.reduce((sum, p) => sum + p.current_uses, 0) || 0,
    totalHitsGiven: promoCodes?.reduce((sum, p) => sum + (p.current_uses * p.hits_amount), 0) || 0,
    totalAutoUnlocks: autoUnlockData?.length || 0,
  };

  return (
    <AdminLayout title="Промокоды" description="Управление промокодами для блогеров и партнёров">
      <div className="space-y-6">
        {/* Stats */}
        <div className="grid gap-4 md:grid-cols-5">
          <Card className="bg-card border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Всего промокодов</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total}</div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Активных</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-500">{stats.active}</div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Всего использований</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalUses}</div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Хитов выдано</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-primary">{stats.totalHitsGiven}</div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                <Unlock className="w-3 h-3" /> Авто-разблокировок
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-primary">{stats.totalAutoUnlocks}</div>
              {stats.totalUses > 0 && (
                <p className="text-xs text-muted-foreground mt-1">
                  {Math.round((stats.totalAutoUnlocks / stats.totalUses) * 100)}% от всех активаций
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Create button and table */}
        <Card className="bg-card border-border">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Ticket className="w-5 h-5 text-primary" />
                Промокоды
              </CardTitle>
              <CardDescription>Создавайте промокоды для блогеров и партнёров</CardDescription>
            </div>
            <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="w-4 h-4 mr-2" />
                  Создать промокод
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Новый промокод</DialogTitle>
                  <DialogDescription>
                    Создайте промокод, который даст бесплатные хиты
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="code">Код промокода</Label>
                    <div className="flex gap-2">
                      <Input
                        id="code"
                        value={newCode}
                        onChange={(e) => setNewCode(e.target.value.toUpperCase())}
                        placeholder="BLOGGER2024"
                        className="font-mono uppercase"
                      />
                      <Button variant="outline" onClick={generateRandomCode}>
                        Сгенерировать
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="hits">Количество бесплатных хитов</Label>
                    <Input
                      id="hits"
                      type="number"
                      min="1"
                      value={newHitsAmount}
                      onChange={(e) => setNewHitsAmount(e.target.value)}
                      placeholder="5"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="maxUses">Максимум использований (пусто = без лимита)</Label>
                    <Input
                      id="maxUses"
                      type="number"
                      min="1"
                      value={newMaxUses}
                      onChange={(e) => setNewMaxUses(e.target.value)}
                      placeholder="100"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>Одноразовый на пользователя</Label>
                      <p className="text-xs text-muted-foreground">Каждый пользователь может использовать только 1 раз</p>
                    </div>
                    <Switch
                      checked={newIsSingleUse}
                      onCheckedChange={setNewIsSingleUse}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="expires">Срок действия (пусто = бессрочно)</Label>
                    <Input
                      id="expires"
                      type="date"
                      value={newExpiresAt}
                      onChange={(e) => setNewExpiresAt(e.target.value)}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                    Отмена
                  </Button>
                  <Button onClick={handleCreate} disabled={createPromoCode.isPending}>
                    {createPromoCode.isPending ? "Создание..." : "Создать"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-8 text-muted-foreground">Загрузка...</div>
            ) : promoCodes?.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                Нет промокодов. Создайте первый!
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Код</TableHead>
                    <TableHead>Хитов</TableHead>
                    <TableHead>Тип</TableHead>
                    <TableHead>Использований</TableHead>
                    <TableHead>🔓 Авто</TableHead>
                    <TableHead>Срок действия</TableHead>
                    <TableHead>Статус</TableHead>
                    <TableHead className="text-right">Действия</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {promoCodes?.map((promo) => {
                    const isExpired = promo.expires_at && new Date(promo.expires_at) < new Date();
                    const isMaxedOut = promo.max_uses !== null && promo.current_uses >= promo.max_uses;
                    
                    return (
                      <TableRow key={promo.id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <code className="bg-muted px-2 py-1 rounded font-mono text-sm">
                              {promo.code}
                            </code>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={() => handleCopyCode(promo.code, promo.id)}
                            >
                              {copiedId === promo.id ? (
                                <Check className="h-3 w-3 text-green-500" />
                              ) : (
                                <Copy className="h-3 w-3" />
                              )}
                            </Button>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary">{promo.hits_amount} хитов</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={promo.is_single_use ? "outline" : "secondary"}>
                            {promo.is_single_use ? "1 раз/юзер" : "Многоразовый"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {promo.current_uses}
                          {promo.max_uses !== null && ` / ${promo.max_uses}`}
                        </TableCell>
                        <TableCell>
                          {autoUnlocksByPromo.get(promo.id) || 0}
                        </TableCell>
                        <TableCell>
                          {promo.expires_at ? (
                            <span className={isExpired ? "text-destructive" : ""}>
                              {format(new Date(promo.expires_at), "d MMM yyyy", { locale: ru })}
                              {isExpired && " (истёк)"}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">Бессрочно</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {isExpired ? (
                            <Badge variant="destructive">Истёк</Badge>
                          ) : isMaxedOut ? (
                            <Badge variant="secondary">Исчерпан</Badge>
                          ) : promo.is_active ? (
                            <Badge variant="default" className="bg-green-600">Активен</Badge>
                          ) : (
                            <Badge variant="outline">Неактивен</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Switch
                              checked={promo.is_active}
                              onCheckedChange={() => handleToggleActive(promo)}
                              disabled={isExpired || isMaxedOut}
                            />
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-destructive hover:text-destructive"
                              onClick={() => handleDelete(promo.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
